import { useRef, useEffect } from 'react';
import './Noise.css';

const Noise = ({
  patternSize = 250,
  patternScaleX = 1,
  patternScaleY = 1,
  patternRefreshInterval = 2,
  patternAlpha = 15
}) => {
  const grainRef = useRef(null);

  useEffect(() => {
    const canvas = grainRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    let intervalId = 0;
    let pattern = null;
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    const tileSize = Math.max(48, Math.min(patternSize || 250, 128));
    const alpha = Math.max(0, Math.min(patternAlpha, 255));
    const refreshMs = Math.max(90, patternRefreshInterval * 45);

    const noiseTile = document.createElement('canvas');
    noiseTile.width = tileSize;
    noiseTile.height = tileSize;
    const noiseCtx = noiseTile.getContext('2d', { alpha: true });
    if (!noiseCtx) return;

    const tileImageData = noiseCtx.createImageData(tileSize, tileSize);
    const pixels = new Uint32Array(tileImageData.data.buffer);

    const rebuildPattern = () => {
      for (let i = 0; i < pixels.length; i++) {
        const value = (Math.random() * 255) | 0;
        pixels[i] = (alpha << 24) | (value << 16) | (value << 8) | value;
      }

      noiseCtx.putImageData(tileImageData, 0, 0);
      pattern = ctx.createPattern(noiseTile, 'repeat');
    };

    const resize = () => {
      const width = Math.ceil(window.innerWidth * dpr);
      const height = Math.ceil(window.innerHeight * dpr);
      canvas.width = width;
      canvas.height = height;
      canvas.style.width = '100vw';
      canvas.style.height = '100vh';
      ctx.setTransform(1, 0, 0, 1, 0, 0);
    };

    const draw = () => {
      if (!pattern) return;
      ctx.save();
      ctx.setTransform(patternScaleX, 0, 0, patternScaleY, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = pattern;
      ctx.fillRect(0, 0, canvas.width / patternScaleX, canvas.height / patternScaleY);
      ctx.restore();
    };

    const tick = () => {
      if (document.hidden) return;
      rebuildPattern();
      draw();
    };

    const start = () => {
      window.clearInterval(intervalId);
      intervalId = window.setInterval(tick, refreshMs);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        window.clearInterval(intervalId);
      } else {
        tick();
        start();
      }
    };

    rebuildPattern();
    window.addEventListener('resize', resize);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    resize();
    draw();
    start();

    return () => {
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [patternSize, patternScaleX, patternScaleY, patternRefreshInterval, patternAlpha]);

  return <canvas className="noise-overlay" ref={grainRef} style={{ imageRendering: 'pixelated' }} />;
};

export default Noise;

